// bluaIQ — P2: Gantt Chart Module
// Interactive Gantt chart with horizontal bars, drag to adjust, critical path

import { apiFetch } from '../utils/api'
import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarDays, AlertTriangle, ChevronDown, ChevronUp, GripHorizontal, Wand2, Loader2, ShieldCheck, FileWarning, MapPin, Info } from 'lucide-react'
import { DEMO_GANTT_TASKS, type GanttTask } from '../utils/ganttDemoData'
import { cascadeDates, findCriticalPath } from '../utils/ganttCascade'
import { isDemoProject } from '../utils/demoData'
import {
  blockerReasonHeading,
  blockerReasonText,
  requiredItemsHeading,
  materialLeadTimeAdvisory,
  materialOrderByDate,
} from '../utils/ganttBlockers'
import { phaseRank, phaseDisplayLabel, macroPhase, macroPhaseLabel, MACRO_PHASE_ORDER, type GanttMacroPhase } from '../utils/ganttPhaseOrder'
import { displayTaskName } from '../utils/ganttTaskLabels'
import { phaseColor, buildPhaseLegend, CRITICAL_COLOR, TODAY_COLOR } from '../utils/ganttColors'
import { hasLegacyBundledTasks } from '../utils/ganttLegacySchedule'

// ── HELPERS ───────────────────────────────────────

const DAY_MS = 86_400_000

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / DAY_MS)
}

function addDays(date: string, days: number): string {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function ganttStatusLabel(status: string | undefined, isEs: boolean): string | null {
  switch (status) {
    case 'PENDING':
      return isEs ? 'Pendiente' : 'Pending'
    case 'IN_PROGRESS':
      return isEs ? 'En progreso' : 'In progress'
    case 'COMPLETED':
      return isEs ? 'Completada' : 'Completed'
    case 'BLOCKED':
      return isEs ? 'Bloqueada' : 'Blocked'
    default:
      return status || null
  }
}

function ganttStatusClasses(status: string | undefined): string {
  switch (status) {
    case 'COMPLETED':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    case 'IN_PROGRESS':
      return 'bg-sky-50 text-sky-700 border border-sky-200'
    case 'BLOCKED':
      return 'bg-amber-50 text-amber-800 border border-amber-200'
    case 'PENDING':
      return 'bg-[#F5F3EF] text-[#756F67] border border-[#E6E0D7]'
    default:
      return 'bg-[#F5F3EF] text-[#756F67] border border-[#E6E0D7]'
  }
}

type InspectionTask = {
  key: string
  label: string
  phase: string
  authority: 'city' | 'public_works' | 'third_party' | 'engineer' | 'permit_specific'
  required: boolean
  blocking: boolean
  predecessors?: string[]
  requiredUploads?: string[]
  notes?: string
}

type JurisdictionPackResponse = {
  supported: boolean
  reason?: string
  location?: {
    address?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
  }
  pack?: {
    city: string
    state: string
    key?: string
    reviewedAt: string
    inspectionWorkflow?: {
      sequenceMode: 'fixed_city_sequence' | 'permit_specific' | 'hybrid'
      tasks: InspectionTask[]
    }
    defaults?: {
      defaultAtticInsulation: string
      defaultWindowPerformance: string
      defaultPlumbingDrain: string
    }
    automationPolicy?: {
      safeToAutomate: string[]
      alwaysVerify: string[]
    }
  }
  blockingInspectionTasks?: InspectionTask[]
}

type InspectionWorkflowStatusResponse = JurisdictionPackResponse & {
  completedTaskKeys?: string[]
  uploadedDocs?: string[]
  readyToScheduleTasks?: InspectionTask[]
  uploadBlockedTasks?: InspectionTask[]
}

// ── COMPONENT ─────────────────────────────────────

export default function GanttModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  // fix(gantt-i18n): force a re-render when the language is toggled live so the
  // phase headers (and every other isEs-derived label) reflect the new language
  // without a page refresh. useTranslation should already do this, but this
  // explicit subscription guarantees it even if the implicit binding misses.
  const [, setLangTick] = useState(0)
  useEffect(() => {
    const onLanguageChanged = () => setLangTick(t => t + 1)
    i18n.on('languageChanged', onLanguageChanged)
    return () => { i18n.off('languageChanged', onLanguageChanged) }
  }, [i18n])
  const queryClient = useQueryClient()
  // Real projects start with an empty schedule — never expose fake demo
  // tasks to real users. Demo projects (matched by isDemoProject) seed
  // DEMO_GANTT_TASKS so the demo experience is unchanged.
  const [tasks, setTasks] = useState<GanttTask[]>(isDemoProject(projectId) ? DEMO_GANTT_TASKS : [])
  const [editingTask, setEditingTask] = useState<string | null>(null)
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set())
  const [generating, setGenerating] = useState(false)
  const [generatingJurisdiction, setGeneratingJurisdiction] = useState(false)
  // Destructive-regeneration confirm modal. Set when the backend returns 409
  // SCHEDULE_EXISTS; cleared on cancel or after a confirmed replace.
  const [replaceConfirm, setReplaceConfirm] = useState<{ mode: 'classic' | 'jurisdiction'; taskCount: number } | null>(null)
  // Surfaced error for a failed generate/replace — never fail silently.
  const [autoGenError, setAutoGenError] = useState<string | null>(null)

  // Single entry point for auto-generate. First call sends NO confirm; if the
  // project already has a schedule the backend returns 409 SCHEDULE_EXISTS and
  // we open the confirm modal. The destructive replace only runs with confirm:true
  // (sent from the modal's "Replace Schedule" action). Never auto-confirms.
  const runAutoGenerate = useCallback(async (mode: 'classic' | 'jurisdiction', confirm: boolean) => {
    const setBusy = mode === 'jurisdiction' ? setGeneratingJurisdiction : setGenerating
    setBusy(true)
    setAutoGenError(null)
    try {
      const token = await getToken()
      const body = mode === 'jurisdiction'
        ? { mode, projectType: 'single_family', ...(confirm ? { confirm: true } : {}) }
        : { mode, ...(confirm ? { confirm: true } : {}) }
      const res = await apiFetch(`/api/projects/${projectId}/lifecycle/auto-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      })
      if (res.status === 409) {
        const data = await res.json().catch(() => ({} as any))
        if (data?.error === 'SCHEDULE_EXISTS') {
          setReplaceConfirm({ mode, taskCount: Number(data.taskCount) || 0 })
          return
        }
      }
      if (res.ok) {
        // Force an immediate, awaited refetch so the regenerated schedule (and
        // the stale-bundle banner recompute) appear without a manual refresh,
        // THEN close the confirm modal.
        await queryClient.refetchQueries({ queryKey: ['gantt-tasks', projectId] })
        queryClient.invalidateQueries({ queryKey: ['jurisdiction-pack', projectId] })
        queryClient.invalidateQueries({ queryKey: ['inspection-workflow-status', projectId] })
        setReplaceConfirm(null)
        return
      }
      // Any other response (400 / 500 / unexpected 409) — surface it, never fail
      // silently. The modal (if open) stays open so the user can retry or cancel.
      const err = await res.json().catch(() => ({} as any))
      setAutoGenError(err?.message || err?.error || (isEs ? 'No se pudo generar el cronograma. Intenta de nuevo.' : 'Could not generate the schedule. Please try again.'))
    } catch {
      setAutoGenError(isEs ? 'Error de red al generar el cronograma.' : 'Network error while generating the schedule.')
    }
    finally { setBusy(false) }
  }, [projectId, getToken, queryClient, isEs])

  const { data: apiTasks, isLoading, isError } = useQuery<GanttTask[]>({
    queryKey: ['gantt-tasks', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/tasks`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const { data: jurisdictionData } = useQuery<JurisdictionPackResponse>({
    queryKey: ['jurisdiction-pack', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/jurisdiction-pack`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('jurisdiction-pack unavailable')
      return res.json()
    },
    retry: false,
    enabled: !!projectId,
  })

  const { data: workflowStatus } = useQuery<InspectionWorkflowStatusResponse>({
    queryKey: ['inspection-workflow-status', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/inspection-workflow/status`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('inspection-workflow-status unavailable')
      return res.json()
    },
    retry: false,
    enabled: !!projectId && !!jurisdictionData?.supported,
  })

  const activeTasks = Array.isArray(apiTasks) ? apiTasks : tasks
  const blockingInspectionTasks = jurisdictionData?.blockingInspectionTasks || []
  const readyToScheduleTasks = workflowStatus?.readyToScheduleTasks || []
  const uploadBlockedTasks = workflowStatus?.uploadBlockedTasks || []
  const jurisdictionPack = jurisdictionData?.pack
  const sequenceMode = jurisdictionPack?.inspectionWorkflow?.sequenceMode
  const prefersJurisdiction = !!jurisdictionData?.supported

  const sequenceModeLabel = sequenceMode
    ? {
        fixed_city_sequence: isEs ? 'Secuencia fija por ciudad' : 'Fixed city sequence',
        permit_specific: isEs ? 'Secuencia definida por permiso' : 'Permit-defined sequence',
        hybrid: isEs ? 'Secuencia híbrida' : 'Hybrid sequence',
      }[sequenceMode]
    : null

  const criticalPath = useMemo(() => findCriticalPath(activeTasks), [activeTasks])

  // Timeline range — guard against empty activeTasks (real projects before
  // first schedule generation, or after an API error) to avoid reduce crash.
  // fix(project-control): additionally filter out null/undefined/empty date
  // strings before reduce so that production API tasks with missing date
  // fields cannot cause addDays(undefined, N) → new Date(undefined).toISOString()
  // → "TypeError: Invalid time value" during render (caught by ErrorBoundary).
  const _fallbackDate = new Date().toISOString().slice(0, 10)
  const _rawDates = activeTasks.length > 0
    ? activeTasks.flatMap(t => [t.startDate, t.endDate])
    : []
  // fix(gantt): also reject unparseable strings — "Invalid Date", "null",
  // "undefined", "0000-00-00" all pass the string/length check but cause
  // addDays → new Date(d).toISOString() → "TypeError: Invalid time value".
  const allDates = _rawDates.filter((d): d is string => typeof d === 'string' && d.length > 0 && !isNaN(new Date(d).getTime()))
  const _safeDates = allDates.length > 0 ? allDates : [_fallbackDate]
  const minDate = _safeDates.reduce((a, b) => a < b ? a : b)
  const maxDate = _safeDates.reduce((a, b) => a > b ? a : b)
  const timelineStart = addDays(minDate, -7)
  const timelineEnd = addDays(maxDate, 14)
  const totalDays = daysBetween(timelineStart, timelineEnd)
  // fix(gantt-layout): explicit pixel width for the right timeline track so it
  // can scroll horizontally (≈9px/day, min 640px) while the left task-list
  // column stays fixed. minWidth:100% lets short schedules still fill the pane.
  const timelinePx = Math.max(640, totalDays * 9)

  // Month markers
  const months = useMemo(() => {
    const result: { label: string; offset: number }[] = []
    const start = new Date(timelineStart)
    const end = new Date(timelineEnd)
    const cur = new Date(start.getFullYear(), start.getMonth(), 1)
    while (cur <= end) {
      const offset = daysBetween(timelineStart, cur.toISOString().slice(0, 10))
      const label = cur.toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', year: '2-digit' })
      result.push({ label, offset: Math.max(0, offset) })
      cur.setMonth(cur.getMonth() + 1)
    }
    return result
  }, [timelineStart, timelineEnd, isEs])

  // Group tasks by phase
  const phases = useMemo(() => {
    const map = new Map<string, GanttTask[]>()
    for (const t of activeTasks) {
      // fix(gantt-i18n): group by the RAW, language-independent phase key so the
      // grouping, ranking and collapse state stay stable across a language
      // toggle. The readable header is localized at render via phaseDisplayLabel
      // using the current isEs, so switching language updates labels live
      // without regrouping or losing collapse state.
      const phase = t.phase
      if (!map.has(phase)) map.set(phase, [])
      map.get(phase)!.push(t)
    }
    // fix(gantt): order the phase GROUPS by a generic construction-sequence
    // rank so the left task list reads coherently even when the backend
    // sortOrder interleaves phases. Within-phase task order is untouched
    // (insertion order = sortOrder). Unrecognised phases keep their original
    // first-encounter order and sort after ranked phases (stable secondary
    // sort by index). The chart render and the dependency-arrow row math both
    // iterate this same array, so row positions stay consistent.
    return Array.from(map.entries())
      .map((entry, index) => ({ entry, index, rank: phaseRank(entry[0]) }))
      .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
      .map(x => x.entry)
  }, [activeTasks])

  // Macro-phase grouping (display-only): bucket the rank-ordered subphase groups
  // into Preconstruction / Construction / Closeout, derived FE-only from the
  // subphase key. The left list, the timeline column, and the dependency-arrow
  // row math all iterate this SAME structure so rows stay aligned.
  const macroGroups = useMemo(() => {
    const byMacro = new Map<GanttMacroPhase, [string, GanttTask[]][]>()
    for (const entry of phases) {
      const m = macroPhase(entry[0])
      if (!byMacro.has(m)) byMacro.set(m, [])
      byMacro.get(m)!.push(entry)
    }
    return MACRO_PHASE_ORDER
      .filter(m => byMacro.has(m))
      .map(m => [m, byMacro.get(m)!] as [GanttMacroPhase, [string, GanttTask[]][]])
  }, [phases])

  // Non-destructive: detect schedules generated before PR #233 (still holding
  // comma-bundled Classic rows) so we can prompt a regenerate. Read-only — never
  // mutates/regenerates/splits anything.
  const hasLegacyBundles = useMemo(() => hasLegacyBundledTasks(activeTasks), [activeTasks])

  const togglePhase = (phase: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev)
      next.has(phase) ? next.delete(phase) : next.add(phase)
      return next
    })
  }

  // Drag state
  const chartRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ taskId: string; startX: number; chartWidth: number; deltaDays: number } | null>(null)
  const [dragState, setDragState] = useState<{ taskId: string; deltaDays: number } | null>(null)

  // Find all downstream dependents (BFS)
  const getDownstream = useCallback((taskId: string): Set<string> => {
    const result = new Set<string>()
    const queue = [taskId]
    while (queue.length > 0) {
      const parentId = queue.shift()!
      for (const t of activeTasks) {
        if (result.has(t.id) || t.id === taskId) continue
        if (t.dependencies.includes(parentId)) {
          result.add(t.id)
          queue.push(t.id)
        }
      }
    }
    return result
  }, [activeTasks])

  const cascadeTargets = useMemo(() => {
    if (!dragState) return new Set<string>()
    return getDownstream(dragState.taskId)
  }, [dragState, getDownstream])

  const handleDragStart = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const chart = chartRef.current
    if (!chart) return
    dragRef.current = { taskId, startX: e.clientX, chartWidth: chart.getBoundingClientRect().width, deltaDays: 0 }
    setDragState({ taskId, deltaDays: 0 })

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return
      const dx = ev.clientX - dragRef.current.startX
      const daysDelta = Math.round((dx / dragRef.current.chartWidth) * totalDays)
      dragRef.current.deltaDays = daysDelta
      setDragState({ taskId: dragRef.current.taskId, deltaDays: daysDelta })
    }

    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      if (!dragRef.current || dragRef.current.deltaDays === 0) {
        dragRef.current = null
        setDragState(null)
        return
      }
      const delta = dragRef.current.deltaDays
      const tid = dragRef.current.taskId
      dragRef.current = null
      setDragState(null)

      // Apply cascade and persist
      const task = activeTasks.find(t => t.id === tid)
      if (task) {
        const newStart = addDays(task.startDate, delta)
        const newEnd = addDays(task.endDate, delta)
        const updated = cascadeDates(activeTasks, tid, newStart, newEnd)
        setTasks(updated)

        // Persist all changed tasks to backend
        ;(async () => {
          const token = await getToken()
          const writes: Promise<unknown>[] = []
          for (const u of updated) {
            const orig = activeTasks.find(t => t.id === u.id)
            if (orig && (orig.startDate !== u.startDate || orig.endDate !== u.endDate)) {
              writes.push(apiFetch(`/api/projects/${projectId}/lifecycle/${u.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({ startDate: u.startDate, endDate: u.endDate }),
              }).catch(() => {}))
            }
          }
          // fix(gantt): once the drag/cascade PATCH writes settle, invalidate
          // the gantt-tasks query so real projects (which render apiTasks) show
          // the persisted dates instead of snapping back to the now-stale data.
          await Promise.allSettled(writes)
          queryClient.invalidateQueries({ queryKey: ['gantt-tasks', projectId] })
        })()
      }
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Today marker
  const today = new Date().toISOString().slice(0, 10)
  const todayOffset = daysBetween(timelineStart, today)

  const formatDate = (d: string) => new Date(d).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })

  // KPIs
  const completedTasks = activeTasks.filter(t => t.progress === 100).length
  const inProgressTasks = activeTasks.filter(t => t.progress > 0 && t.progress < 100).length
  const criticalCount = activeTasks.filter(t => criticalPath.has(t.id)).length

  return (
    <div className="p-5 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
      <div>
        <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
          <CalendarDays size={18} />
          {isEs ? 'Cronograma Gantt' : 'Gantt Schedule'}
        </h2>
        <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
          {activeTasks.length} {isEs ? 'tareas' : 'tasks'} · {formatDate(minDate)} — {formatDate(maxDate)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => runAutoGenerate(prefersJurisdiction ? 'jurisdiction' : 'classic', false)}
          disabled={generating || generatingJurisdiction}
          className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#14457E] disabled:opacity-40 transition-colors"
        >
          {generating || generatingJurisdiction ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {prefersJurisdiction
            ? (isEs ? 'Auto-Generar Recomendado' : 'Auto-Generate Recommended')
            : (isEs ? 'Generar Cronograma Base' : 'Generate Base Schedule')}
        </button>
        <button
          onClick={() => runAutoGenerate('jurisdiction', false)}
          disabled={generatingJurisdiction || !jurisdictionData?.supported}
          className="flex items-center gap-1.5 border border-[#06539B] text-[#06539B] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#EFF6FF] disabled:opacity-40 transition-colors"
        >
          {generatingJurisdiction ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {isEs ? 'Generar por Jurisdicción' : 'Generate From Jurisdiction'}
        </button>
      </div>
      </div>

      {/* Generate error (first click / no modal) — never fail silently. */}
      {autoGenError && !replaceConfirm && (
        <div
          data-testid="gantt-autogen-error-inline"
          className="flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 text-[12px] font-[700] text-red-700"
        >
          <AlertTriangle size={15} className="shrink-0" />
          {autoGenError}
        </div>
      )}

      {/* Destructive-regeneration confirmation modal — shown only on a 409
          SCHEDULE_EXISTS from the backend guard. The destructive replace runs
          ONLY when the user clicks "Replace Schedule" (sends confirm:true).
          Cancel / backdrop click does nothing — no automatic regeneration. */}
      {replaceConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="gantt-replace-confirm"
          onClick={() => setReplaceConfirm(null)}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="shrink-0 mt-0.5 text-amber-600" />
              <div className="space-y-1">
                <h3 className="text-[14px] font-[900] text-[#1C1A17]">
                  {isEs ? 'Reemplazar cronograma' : 'Replace schedule'}
                </h3>
                <p className="text-[12px] font-[600] text-[#4B5563]">
                  {isEs
                    ? 'Esto reemplazará el cronograma actual del proyecto, incluyendo fechas, estados, notas, bloqueos y dependencias. ¿Deseas continuar?'
                    : 'This will replace the current project schedule, including task dates, statuses, notes, blockers, and dependencies. Continue?'}
                </p>
                {replaceConfirm.taskCount > 0 && (
                  <p className="text-[11px] font-[700] text-amber-700">
                    {replaceConfirm.taskCount} {isEs ? 'tareas serán reemplazadas.' : 'tasks will be replaced.'}
                  </p>
                )}
                {autoGenError && (
                  <p className="text-[11px] font-[700] text-red-600" data-testid="gantt-autogen-error">{autoGenError}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setReplaceConfirm(null)}
                disabled={generating || generatingJurisdiction}
                className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[12px] font-[800] text-[#4B5563] hover:bg-[#F5F3EF] disabled:opacity-40"
              >
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={() => runAutoGenerate(replaceConfirm.mode, true)}
                disabled={generating || generatingJurisdiction}
                className="flex-1 bg-[#EF4444] text-white rounded-full py-2 text-[12px] font-[800] hover:bg-[#DC2626] disabled:opacity-40"
              >
                {(generating || generatingJurisdiction) ? '…' : (isEs ? 'Reemplazar Cronograma' : 'Replace Schedule')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Completadas' : 'Complete'}</div>
          <div className="text-[18px] font-[900] text-[#22C55E]">{completedTasks}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'En Progreso' : 'In Progress'}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{inProgressTasks}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Pendientes' : 'Upcoming'}</div>
          <div className="text-[18px] font-[900] text-[#9E9890]">{activeTasks.length - completedTasks - inProgressTasks}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Ruta Crítica' : 'Critical Path'}</div>
          <div className="text-[18px] font-[900] text-[#EF4444]">{criticalCount}</div>
        </div>
      </div>

      {/* Financial Trust P0 (PR-F6): base schedule honesty + CO not-connected note */}
      <div
        data-testid="gantt-base-schedule-note"
        className="flex items-start gap-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2.5"
      >
        <Info size={13} className="text-[#9E9890] mt-0.5 shrink-0" />
        <p className="text-[10px] font-[600] text-[#6B6660] leading-relaxed">
          {isEs
            ? 'Las fechas del Gantt son de planificación hasta confirmarse con contratos y hitos reales. El impacto de órdenes de cambio se registra por separado y aún no se aplica al Gantt. El Gantt aún no está conectado al calendario de pagos confirmados.'
            : 'Gantt dates are planning dates until confirmed with real contracts and milestones. Change order impact is tracked separately and not yet applied to the Gantt. The Gantt is not yet connected to the confirmed payment schedule.'}
        </p>
      </div>

      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-[14px] font-[900] text-[#1C1A17] flex items-center gap-2">
              <ShieldCheck size={15} className="text-[#06539B]" />
              {isEs ? 'Jurisdicción e Inspecciones' : 'Jurisdiction & Inspections'}
            </h3>
            <p className="text-[11px] font-[600] text-[#9E9890] mt-1">
              {isEs
                ? 'Esta capa nueva prepara el cronograma para reglas por ciudad, bloqueos y documentos requeridos.'
                : 'This new layer prepares the schedule for city-specific rules, blockers, and required documents.'}
            </p>
          </div>
          {jurisdictionData?.location?.city && (
            <div className="flex items-center gap-1.5 text-[11px] font-[700] text-[#06539B] bg-[#EFF6FF] px-3 py-1.5 rounded-full whitespace-nowrap">
              <MapPin size={12} />
              {jurisdictionData.location.city}, {jurisdictionData.location.state || 'TX'}
            </div>
          )}
        </div>

        {!jurisdictionData?.supported ? (
          <div className="mt-4 border border-amber-200 bg-amber-50 rounded-xl p-3">
            <div className="text-[12px] font-[800] text-amber-900">
              {isEs ? 'Pack de jurisdicción no disponible todavía' : 'Jurisdiction pack not available yet'}
            </div>
            <div className="text-[11px] font-[600] text-amber-800 mt-1">
              {isEs ? 'Aún no hay un pack de jurisdicción disponible para esta ubicación del proyecto.' : (jurisdictionData?.reason || 'The project location does not have a supported pack yet.')}
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 xl:grid-cols-4 gap-4">
            <div className="border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
                {isEs ? 'Pack Activo' : 'Active Pack'}
              </div>
              <div className="text-[15px] font-[900] text-[#1C1A17]">
                {jurisdictionPack?.city}, {jurisdictionPack?.state}
              </div>
              {sequenceModeLabel && (
                <div className="mt-2 text-[11px] font-[700] text-[#06539B]">
                  {isEs ? 'Modo:' : 'Mode:'} {sequenceModeLabel}
                </div>
              )}
              <div className="mt-3 space-y-1.5 text-[11px] font-[600] text-[#4B5563]">
                <div>
                  {isEs ? 'Aislante ático:' : 'Attic insulation:'} {jurisdictionPack?.defaults?.defaultAtticInsulation || '—'}
                </div>
                <div>
                  {isEs ? 'Ventanas:' : 'Windows:'} {jurisdictionPack?.defaults?.defaultWindowPerformance || '—'}
                </div>
                <div>
                  {isEs ? 'Drenaje:' : 'Drainage:'} {jurisdictionPack?.defaults?.defaultPlumbingDrain || '—'}
                </div>
              </div>
            </div>

            <div className="border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
                {isEs ? 'Blockers Principales' : 'Main Blockers'}
              </div>
              {blockingInspectionTasks.length === 0 ? (
                <div className="text-[11px] font-[600] text-[#9E9890]">
                  {isEs ? 'No hay bloqueos publicados para este pack.' : 'No published blockers for this pack yet.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {blockingInspectionTasks.slice(0, 4).map(task => (
                    <div key={task.key} className="rounded-lg bg-[#FAFAF8] border border-[#ECEAE4] p-2.5">
                      <div className="text-[11px] font-[800] text-[#1C1A17]">{task.label}</div>
                      <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                        {isEs ? 'Fase:' : 'Phase:'} {task.phase}
                      </div>
                      {!!task.requiredUploads?.length && (
                        <div className="text-[10px] font-[700] text-amber-700 mt-1">
                          {isEs ? 'Uploads requeridos:' : 'Required uploads:'} {task.requiredUploads.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
                {isEs ? 'Listas para Programar' : 'Ready To Schedule'}
              </div>
              {readyToScheduleTasks.length === 0 ? (
                <div className="text-[11px] font-[600] text-[#9E9890]">
                  {isEs ? 'Todavía no hay inspecciones listas.' : 'No inspections are ready yet.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {readyToScheduleTasks.slice(0, 4).map(task => (
                    <div key={task.key} className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5">
                      <div className="text-[11px] font-[800] text-emerald-900">{task.label}</div>
                      <div className="text-[10px] font-[600] text-emerald-800 mt-0.5">
                        {isEs ? 'Autoridad:' : 'Authority:'} {task.authority}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
                {isEs ? 'Esperando Uploads / Verificar' : 'Waiting On Uploads / Verify'}
              </div>
              {uploadBlockedTasks.length > 0 ? (
                <div className="space-y-2">
                  {uploadBlockedTasks.slice(0, 3).map(task => (
                    <div key={task.key} className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
                      <div className="text-[11px] font-[800] text-amber-900">{task.label}</div>
                      <div className="text-[10px] font-[700] text-amber-800 mt-1">
                        {isEs ? 'Faltan:' : 'Missing:'} {(task.requiredUploads || []).join(', ')}
                      </div>
                    </div>
                  ))}
                  {!!jurisdictionPack?.automationPolicy?.alwaysVerify?.length && (
                    <div className="pt-1 space-y-2">
                      {jurisdictionPack.automationPolicy.alwaysVerify.slice(0, 3).map(item => (
                        <div key={item} className="flex items-start gap-2 text-[11px] font-[600] text-[#4B5563]">
                          <FileWarning size={12} className="text-amber-600 mt-0.5 shrink-0" />
                          <span className="break-words">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : jurisdictionPack?.automationPolicy?.alwaysVerify?.length ? (
                <div className="space-y-2">
                  {jurisdictionPack.automationPolicy.alwaysVerify.slice(0, 5).map(item => (
                    <div key={item} className="flex items-start gap-2 text-[11px] font-[600] text-[#4B5563]">
                      <FileWarning size={12} className="text-amber-600 mt-0.5 shrink-0" />
                      <span className="break-words">{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] font-[600] text-[#9E9890]">
                  {isEs ? 'Sin flags adicionales todavía.' : 'No additional verify flags yet.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legacy bundled-schedule notice — non-destructive. Shown when the
          project still holds pre-#233 comma-bundled Classic rows. Guidance only:
          it points to the existing "Generate Base Schedule" action and never
          regenerates, overwrites, or modifies anything on its own. */}
      {hasLegacyBundles && activeTasks.length > 0 && (
        <div
          data-testid="gantt-legacy-bundle-banner"
          className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-[12px] text-amber-900"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
          <div className="space-y-0.5">
            <p className="font-[700]">
              {isEs
                ? 'Este cronograma fue generado con una plantilla base anterior. Regenera el Cronograma Base para dividir tareas agrupadas en actividades individuales.'
                : 'This schedule was generated with an older base template. Regenerate the Base Schedule to split bundled rows into individual actionable tasks.'}
            </p>
            <p className="font-[600] text-amber-700">
              {isEs
                ? 'Regenerar reemplazará el cronograma actual.'
                : 'Regenerating will replace the current schedule.'}
            </p>
          </div>
        </div>
      )}

      {/* Gantt chart — honest empty/error/loading state for real projects */}
      {activeTasks.length === 0 ? (
        <div className="bg-white border border-[#ECEAE4] rounded-xl flex flex-col items-center justify-center py-20 gap-3">
          {isLoading ? (
            <Loader2 size={24} className="animate-spin text-[#9E9890]" />
          ) : isError ? (
            <div className="flex flex-col items-center gap-3" data-testid="gantt-api-error">
              <AlertTriangle size={24} className="text-[#EF4444]" />
              <p className="text-[13px] font-[700] text-[#756F67]">
                {isEs ? 'No se pudo cargar el cronograma.' : 'Schedule could not be loaded.'}
              </p>
              <p className="text-[11px] font-[600] text-[#9E9890]">
                {isEs ? 'Intenta recargar la página.' : 'Try refreshing the page.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3" data-testid="gantt-empty-state">
              <CalendarDays size={24} className="text-[#ECEAE4]" />
              <p className="text-[13px] font-[700] text-[#756F67]">
                {isEs ? 'Aún no hay tareas en el cronograma.' : 'No schedule tasks yet.'}
              </p>
              <p className="text-[11px] font-[600] text-[#9E9890]">
                {isEs ? 'Usa Auto-Generar para crear el cronograma.' : 'Use Auto-Generate to create the schedule.'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <div className="flex">
          {/* LEFT — task list column. fix(gantt-layout): activity labels and
              statuses live here in a clear, fixed-width list column instead of
              floating over the calendar. The timeline scrolls horizontally on
              the right; this column stays put. Row heights (h-8 header, h-10
              phase header, h-10 task) match the right column exactly so rows
              and dependency arrows stay aligned. */}
          <div className="w-[240px] sm:w-[280px] lg:w-[320px] shrink-0 border-r border-[#ECEAE4] bg-white">
            <div className="h-8 flex items-center px-3 bg-[#F2F0EB] border-b border-[#ECEAE4]">
              <span className="text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px]">
                {isEs ? 'Actividad' : 'Activity'}
              </span>
            </div>
            {macroGroups.map(([macro, subphases]) => {
              const macroCount = subphases.reduce((n, [, t]) => n + t.length, 0)
              return (
                <div key={macro}>
                  {/* Macro-phase header band — Preconstruction / Construction / Closeout */}
                  <div className="h-10 flex items-center gap-2 px-3 bg-[#ECE9E2] border-b border-[#DAD6CD]">
                    <span className="text-[10px] font-[900] uppercase tracking-[0.6px] text-[#1C1A17] truncate">{macroPhaseLabel(macro, isEs)}</span>
                    <span className="text-[9px] font-[700] text-[#9E9890] shrink-0">({macroCount})</span>
                  </div>
                  {subphases.map(([phase, phaseTasks]) => {
                  const isCollapsed = expandedPhases.has(phase)
                  return (
                <div key={phase}>
                  <button
                    onClick={() => togglePhase(phase)}
                    className="w-full h-10 flex items-center gap-2 pl-6 pr-3 bg-[#FAFAF8] border-b border-[#ECEAE4] hover:bg-[#F2F0EB] transition-colors text-left"
                  >
                    {isCollapsed ? <ChevronUp size={12} className="text-[#9E9890] shrink-0" /> : <ChevronDown size={12} className="text-[#9E9890] shrink-0" />}
                    <span className="text-[10px] font-[800] text-[#1C1A17] truncate">{phaseDisplayLabel(phase, phaseTasks[0]?.phaseEs, isEs)}</span>
                    <span className="text-[9px] font-[600] text-[#9E9890] shrink-0">({phaseTasks.length})</span>
                  </button>
                  {!isCollapsed && phaseTasks.map(task => {
                    const isCascadeTarget = cascadeTargets.has(task.id)
                    const isCritical = criticalPath.has(task.id)
                    const isEditing = editingTask === task.id
                    const isBlocked = task.status === 'BLOCKED'
                    const statusLabel = ganttStatusLabel(task.status, isEs)
                    return (
                      <div key={task.id} className={`h-10 border-b border-[#F2F0EB] flex items-center px-2 ${isCascadeTarget ? 'bg-yellow-50' : ''}`}>
                        {/* Task label + status — clickable task control (opens the
                            details/edit panel). Keyboard-accessible button. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (!dragRef.current) setEditingTask(isEditing ? null : task.id) }}
                          aria-label={isEs ? 'Abrir detalles de la tarea' : 'Open task details'}
                          // Full localized task name as the hover tooltip, so a row
                          // truncated by the fixed-width column still shows in full.
                          title={displayTaskName(task, isEs)}
                          aria-expanded={isEditing}
                          className={`w-full h-7 flex items-center gap-1.5 pl-2 pr-1.5 rounded-lg border bg-white shadow-sm overflow-hidden cursor-pointer transition-colors text-left hover:bg-[#F5F3EF] hover:shadow ${isBlocked ? 'border-amber-300 hover:border-amber-500' : isCritical ? 'border-red-200 hover:border-[#EF4444]' : 'border-[#E6E0D7] hover:border-[#06539B]'}`}
                        >
                          {isCritical && <AlertTriangle size={9} className="shrink-0 text-[#EF4444]" />}
                          <span className={`text-[10px] font-[700] truncate min-w-0 ${isCascadeTarget ? 'text-amber-700' : 'text-[#1C1A17]'}`}>
                            {displayTaskName(task, isEs)}
                            {isCascadeTarget && dragState && <span className="ml-1 text-[8px] font-[800] shrink-0">+{dragState.deltaDays}d</span>}
                          </span>
                          {statusLabel && (
                            <span className={`shrink-0 ml-auto text-[8px] font-[800] uppercase tracking-[0.35px] px-1.5 py-0.5 rounded-full ${ganttStatusClasses(task.status)}`}>
                              {statusLabel}
                            </span>
                          )}
                        </button>
                      </div>
                    )
                  })}
                </div>
                  )
                  })}
                </div>
              )
            })}
          </div>

          {/* RIGHT — horizontally scrollable timeline column */}
          <div className="flex-1 overflow-x-auto">
          <div style={{ width: `${timelinePx}px`, minWidth: '100%' }}>
        {/* Month header */}
        <div className="relative h-8 bg-[#F2F0EB] border-b border-[#ECEAE4] overflow-hidden">
          {months.map((m, i) => (
            <div
              key={i}
              className="absolute top-0 h-full flex items-center text-[9px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] px-2 border-l border-[#ECEAE4]"
              style={{ left: `${(m.offset / totalDays) * 100}%` }}
            >
              {m.label}
            </div>
          ))}
        </div>

        {/* Tasks */}
        <div className="relative" ref={chartRef}>
          {/* Today line */}
          {todayOffset > 0 && todayOffset < totalDays && (
            <div
              className="absolute top-0 bottom-0 w-px bg-[#EF4444] z-10"
              style={{ left: `${(todayOffset / totalDays) * 100}%` }}
            >
              <div className="absolute -top-0 -translate-x-1/2 bg-[#EF4444] text-white text-[8px] font-[800] px-1.5 py-0.5 rounded-b">
                {isEs ? 'HOY' : 'TODAY'}
              </div>
            </div>
          )}

          {/* Dependency arrows SVG overlay */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-[5]" style={{ overflow: 'visible' }}>
            <defs>
              <marker id="dep-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#9E9890" />
              </marker>
            </defs>
            {activeTasks.flatMap(task =>
              task.dependencies.map(depId => {
                const dep = activeTasks.find(t => t.id === depId)
                // fix(gantt): also guard null/undefined dates so daysBetween
                // never receives an invalid value and SVG positioning stays
                // valid (no NaN% CSS values in the dependency arrow lines).
                if (!dep || !dep.endDate || !task.startDate) return null
                // Calculate positions as percentages
                const depEndPct = ((daysBetween(timelineStart, dep.endDate)) / totalDays) * 100
                const taskStartPct = ((daysBetween(timelineStart, task.startDate)) / totalDays) * 100

                // Find row indices for vertical positioning
                let depRow = 0, taskRow = 0, rowCount = 0
                for (const [, subphases] of macroGroups) {
                  rowCount++ // macro-phase header row
                  for (const [phase, pt] of subphases) {
                    const isCollapsed = expandedPhases.has(phase)
                    rowCount++ // subphase header row
                    if (!isCollapsed) {
                      for (const t of pt) {
                        if (t.id === depId) depRow = rowCount
                        if (t.id === task.id) taskRow = rowCount
                        rowCount++
                      }
                    }
                  }
                }

                if (!depRow || !taskRow) return null
                const y1 = depRow * 40 + 20
                const y2 = taskRow * 40 + 20

                return (
                  <line
                    key={`${depId}-${task.id}`}
                    x1={`${depEndPct}%`} y1={y1}
                    x2={`${taskStartPct}%`} y2={y2}
                    stroke="#9E9890" strokeWidth={1.5} strokeDasharray="4 2"
                    markerEnd="url(#dep-arrow)" opacity={0.6}
                  />
                )
              })
            )}
          </svg>

          {macroGroups.map(([macro, subphases]) => (
            <div key={macro}>
              {/* Macro-phase header spacer — aligns with the left macro band. */}
              <div className="h-10 bg-[#ECE9E2] border-b border-[#DAD6CD]" />
              {subphases.map(([phase, phaseTasks]) => {
            const isCollapsed = expandedPhases.has(phase)
            return (
              <div key={phase}>
                {/* Phase header spacer — keeps timeline rows aligned with the
                    left task-list column, where the clickable phase header and
                    readable phase label live. */}
                <div className="h-10 bg-[#FAFAF8] border-b border-[#ECEAE4]" />

                {/* Phase tasks */}
                {!isCollapsed && phaseTasks.map(task => {
                  const startOffset = daysBetween(timelineStart, task.startDate)
                  const duration = daysBetween(task.startDate, task.endDate)
                  const isDragging = dragState?.taskId === task.id
                  const isCascadeTarget = cascadeTargets.has(task.id)
                  const dragOffset = isDragging ? (dragState.deltaDays / totalDays) * 100 : 0
                  const leftPct = (startOffset / totalDays) * 100 + dragOffset
                  const widthPct = Math.max((duration / totalDays) * 100, 1)
                  const isCritical = criticalPath.has(task.id)
                  const isEditing = editingTask === task.id
                  // Phase 1 generic blocker visibility: the clear "blocked"
                  // treatment (striped bar + amber border + badge) now applies
                  // to ALL tasks marked BLOCKED, not only jurisdiction
                  // inspection tasks. Jurisdiction tasks already received this
                  // treatment, so their appearance is unchanged.
                  const isBlocked = task.status === 'BLOCKED'
                  // Honest legend: the bar fill comes from the task's real phase
                  // via the centralized resolver (same source as the legend), so
                  // every legend color is a color a bar actually uses. Critical /
                  // blocked / today layer ON TOP of this fill (below), never
                  // replacing it.
                  const fill = phaseColor(task.phase)

                  return (
                    <div
                      key={task.id}
                      className={`relative h-10 border-b border-[#F2F0EB] group ${isCascadeTarget ? 'bg-yellow-50' : ''}`}
                    >
                      {/* Gantt bar */}
                      <div
                        className={`absolute top-2 h-6 rounded-md cursor-grab active:cursor-grabbing select-none ${isDragging ? 'shadow-lg z-20' : 'group-hover:shadow-md'}`}
                        style={{
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          background: isBlocked
                            ? `linear-gradient(135deg, ${fill} 0%, ${fill} 58%, #FDE68A 58%, #FDE68A 68%, ${fill} 68%, ${fill} 100%)`
                            : fill,
                          border: isBlocked
                            ? '2px solid #D97706'
                            : isCritical
                              ? '2px solid #EF4444'
                              : isCascadeTarget
                                ? '2px solid #F59E0B'
                                : 'none',
                          minWidth: '20px',
                          opacity: isDragging ? 0.5 : 1,
                          transition: isDragging ? 'none' : 'all 0.15s',
                        }}
                        onMouseDown={(e) => handleDragStart(task.id, e)}
                        onClick={() => { if (!dragRef.current) setEditingTask(isEditing ? null : task.id) }}
                      >
                        {/* Progress fill */}
                        {task.progress > 0 && (
                          <div
                            className="h-full rounded-md opacity-40 bg-white"
                            style={{ width: `${100 - task.progress}%`, marginLeft: `${task.progress}%` }}
                          />
                        )}

                        {/* Critical path indicator */}
                        {isCritical && (
                          <div className="absolute -top-1 -right-1">
                            <AlertTriangle size={10} className="text-[#EF4444]" />
                          </div>
                        )}

                        {isBlocked && (
                          <div className="absolute -top-1.5 left-1">
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[7px] font-[900] uppercase tracking-[0.35px] text-amber-800 border border-amber-200">
                              {isEs ? 'Bloqueada' : 'Blocked'}
                            </span>
                          </div>
                        )}

                        {/* Drag handle */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-60 transition-opacity">
                          <GripHorizontal size={12} className="text-white" />
                        </div>
                      </div>

                      {/* Task detail popup — editable */}
                      {isEditing && (
                        <GanttEditPanel
                          task={task}
                          isEs={isEs}
                          isCritical={isCritical}
                          activeTasks={activeTasks}
                          projectId={projectId!}
                          getToken={getToken}
                          onRefresh={() => {
                            queryClient.invalidateQueries({ queryKey: ['gantt-tasks', projectId] })
                            queryClient.invalidateQueries({ queryKey: ['inspection-workflow-status', projectId] })
                          }}
                          onSave={(updated) => {
                            setTasks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t))
                            setEditingTask(null)
                          }}
                          onClose={() => setEditingTask(null)}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
              </div>
            ))}
        </div>
        </div>
        </div>
        </div>
      </div>
      )}

      {/* Legend — HONEST + DYNAMIC: shows only what is actually on the chart.
          Critical Path appears only when a critical task exists; Today only when
          the today line is in range; phase swatches are the EXACT colors the
          bars use (phaseColor) and list only phases actually present. No
          decorative or unused colors. Labels are bilingual via phaseDisplayLabel. */}
      <div className="flex flex-wrap items-center gap-4 text-[12px] font-[700] text-[#9E9890]">
        {criticalCount > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-2" style={{ borderColor: CRITICAL_COLOR }} />
            {isEs ? 'Ruta Crítica' : 'Critical Path'}
          </div>
        )}
        {todayOffset > 0 && todayOffset < totalDays && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ background: TODAY_COLOR }} />
            {isEs ? 'Hoy' : 'Today'}
          </div>
        )}
        {buildPhaseLegend(activeTasks, isEs).map(item => (
          <div key={item.phase} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ background: item.color }} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── GANTT EDIT PANEL ─────────────────────────────

function GanttEditPanel({ task, isEs, isCritical, activeTasks, projectId, getToken, onRefresh, onSave, onClose }: {
  task: GanttTask; isEs: boolean; isCritical: boolean; activeTasks: GanttTask[]
  projectId: string; getToken: () => Promise<string | null>
  onRefresh: () => void
  onSave: (t: Partial<GanttTask> & { id: string }) => void; onClose: () => void
}) {
  const [startDate, setStartDate] = useState(task.startDate)
  const [endDate, setEndDate] = useState(task.endDate)
  // fix(gantt): initialise from the task's real status first so a BLOCKED task
  // shows "Blocked" in the dropdown (matching the row badge). Fall back to the
  // progress heuristic only when status is absent.
  const [status, setStatus] = useState(task.status ?? (task.progress === 100 ? 'COMPLETED' : task.progress > 0 ? 'IN_PROGRESS' : 'PENDING'))
  // fix(gantt): preload any saved note so reopening a task shows the existing
  // blocker reason (and lets the builder edit it) instead of starting blank.
  const [notes, setNotes] = useState(task.notes ?? '')
  const [saving, setSaving] = useState(false)
  // Visible error for a failed save / inspection-log / unblock — never fail
  // silently. User input above is preserved so the action can be retried.
  const [saveError, setSaveError] = useState<string | null>(null)
  // Jurisdiction-pack inspection tasks carry source JURISDICTION / an externalKey
  // and get the full jurisdiction workflow (authority + required uploads + the
  // pack-specific event set, incl. Retry). Classic/Base Schedule inspection
  // tasks (e.g. "Rough-in inspection" / "Inspecciones rough-in", "City final
  // inspection") have no externalKey and source CLASSIC — but a task NAMED as an
  // inspection must still offer a BASIC inspection log (Visible = Functional).
  // Detect those by name in either language; they reuse the SAME log path with
  // no jurisdiction authority/uploads and no fabricated jurisdiction data.
  const isJurisdictionInspection = task.source === 'JURISDICTION' || !!task.externalKey
  const isClassicInspection =
    !isJurisdictionInspection && /inspecc|inspection/i.test(`${task.name ?? ''} ${task.nameEs ?? ''}`)
  const isInspectionTask = isJurisdictionInspection || isClassicInspection

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/lifecycle/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ startDate, endDate, status, notes: notes || undefined }),
      })
      // Never treat a non-ok response as success — that would optimistically
      // update the row, then silently revert on refetch. Surface it instead.
      if (!res.ok) throw new Error('SAVE_FAILED')
      // fix(gantt): refetch so the row badge/bar reflect the new status/dates
      // immediately. Real projects render apiTasks (the query result), so the
      // optimistic onSave below is invisible to them without invalidation.
      onRefresh()
      const progress = status === 'COMPLETED' ? 100 : status === 'IN_PROGRESS' ? 50 : 0
      onSave({ id: task.id, startDate, endDate, status, progress })
    } catch {
      // Non-ok response OR network error — visible feedback, no success callback,
      // input preserved so the user can retry.
      setSaveError(isEs ? 'No se pudo guardar la tarea. Inténtalo de nuevo.' : 'Couldn’t save the task. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const logInspectionEvent = async (action: 'requested' | 'scheduled' | 'passed' | 'failed' | 'rescheduled' | 'retry' | 'reopen') => {
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/lifecycle/${task.id}/inspection-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          action,
          eventDate: new Date().toISOString(),
          scheduledDate: action === 'scheduled' || action === 'rescheduled' || action === 'retry'
            ? new Date(startDate).toISOString()
            : undefined,
          notes: notes || undefined,
        }),
      })
      if (!res.ok) throw new Error('INSPECTION_EVENT_FAILED')
      onRefresh()
      if (action === 'passed') onSave({ id: task.id, progress: 100 })
      if (action === 'failed') onSave({ id: task.id, progress: 0 })
      // Correction: a mistaken pass/fail is moved back to PENDING.
      if (action === 'reopen') onSave({ id: task.id, status: 'PENDING', progress: 0 })
    } catch {
      setSaveError(isEs ? 'No se pudo registrar el evento de inspección. Inténtalo de nuevo.' : 'Couldn’t log the inspection event. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // Phase 1 "Mark unblocked": reuses the existing lifecycle PATCH path and
  // sends ONLY { status: 'PENDING' }. The backend PATCH spreads only the
  // fields present in the body, so notes, startDate and endDate are left
  // untouched (verified in router.patch /lifecycle/:taskId). No new backend
  // field, no cross-module write, no enforcement.
  const handleUnblock = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/lifecycle/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ status: 'PENDING' }),
      })
      if (!res.ok) throw new Error('UNBLOCK_FAILED')
      onRefresh()
      onSave({ id: task.id, status: 'PENDING', progress: 0 })
    } catch {
      setSaveError(isEs ? 'No se pudo desbloquear la tarea. Inténtalo de nuevo.' : 'Couldn’t unblock the task. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const inputClass = "w-full border border-[#ECEAE4] rounded-lg px-2 py-1 text-[10px] font-[600] focus:outline-none focus:border-[#06539B]"

  return (
    <div className="absolute top-full left-4 z-20 bg-white border border-[#ECEAE4] rounded-xl p-3 shadow-lg min-w-[280px] space-y-2"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-[800] text-[#1C1A17]">{displayTaskName(task, isEs)}</span>
        {isCritical && (
          <span className="text-[8px] font-[800] text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">
            <AlertTriangle size={8} className="inline mr-0.5" />{isEs ? 'CRÍTICA' : 'CRITICAL'}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Inicio' : 'Start'}</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Fin' : 'End'}</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputClass} />
        </div>
      </div>

      <div>
        <label className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Estado' : 'Status'}</label>
        <select value={status} onChange={e => setStatus(e.target.value)} className={inputClass}>
          <option value="PENDING">{isEs ? 'Pendiente' : 'Pending'}</option>
          <option value="IN_PROGRESS">{isEs ? 'En progreso' : 'In Progress'}</option>
          <option value="COMPLETED">{isEs ? 'Completado' : 'Completed'}</option>
          <option value="BLOCKED">{isEs ? 'Bloqueado' : 'Blocked'}</option>
        </select>
      </div>

      <div>
        <label className="text-[9px] font-[700] text-[#9E9890]">{isEs ? 'Notas' : 'Notes'}</label>
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder={isEs ? 'Agregar notas...' : 'Add notes...'}
          className={`${inputClass} resize-none`} />
      </div>

      {task.dependencies.length > 0 && (
        <div className="text-[9px] font-[600] text-[#9E9890]">
          {isEs ? 'Depende de' : 'Depends on'}: {task.dependencies.map(depId => {
            const dep = activeTasks.find(t => t.id === depId)
            return dep ? displayTaskName(dep, isEs) : depId
          }).join(', ')}
        </div>
      )}

      {/* Phase 1 generic blocker reason — display only. Reads the task's own
          notes as a temporary reason (notes is not yet returned by GET /tasks,
          so the honest fallback shows until a future backend change). No
          inference about documents, bids, payments, or other modules. */}
      {task.status === 'BLOCKED' && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-1">
          <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-amber-800">
            {blockerReasonHeading(isEs)}
          </div>
          <div className="text-[10px] font-[600] text-amber-900 break-words">
            {blockerReasonText(task, isEs)}
          </div>
          <button
            onClick={handleUnblock}
            disabled={saving}
            className="mt-1 w-full border border-amber-300 bg-white rounded-full py-1 text-[9px] font-[800] text-amber-800 disabled:opacity-40"
          >
            {isEs ? 'Marcar desbloqueado' : 'Mark unblocked'}
          </button>
        </div>
      )}

      {/* Phase 1 required items — display only. Shows the task's own
          requiredUploads for non-jurisdiction tasks. Does NOT link to
          Documents and does NOT infer whether the items exist. Jurisdiction
          tasks keep their existing "Required uploads" display unchanged. */}
      {!isInspectionTask && !!task.requiredUploads?.length && (
        <div className="rounded-lg bg-[#FAFAF8] border border-[#ECEAE4] p-2 space-y-1">
          <div className="text-[9px] font-[800] uppercase tracking-[0.35px] text-[#9E9890]">
            {requiredItemsHeading(isEs)}
          </div>
          <div className="text-[10px] font-[600] text-[#4B5563] break-words">
            {task.requiredUploads.join(', ')}
          </div>
        </div>
      )}

      {/* Phase 1 generic material lead-time advisory — recommendation only.
          Not connected to Material Orders; no ready-to-order or SKU/pricing
          logic. Worded as planning guidance, never as a fact about an order. */}
      {task.status !== 'COMPLETED' && (
        <div className="rounded-lg bg-[#F5F8FC] border border-[#DCE7F3] p-2">
          <div className="text-[10px] font-[600] text-[#3A5A7A] break-words">
            {materialLeadTimeAdvisory(isEs)}
            {materialOrderByDate(task.startDate) && (
              <span className="font-[700]"> · {isEs ? 'aprox.' : 'by'} {materialOrderByDate(task.startDate)}</span>
            )}
          </div>
        </div>
      )}

      {isInspectionTask && (
        <div className="pt-1">
          <div className="space-y-1 mb-2">
            {!!task.authority && (
              <div className="text-[9px] font-[700] text-[#6B7280]">
                {isEs ? 'Autoridad:' : 'Authority:'} {task.authority}
              </div>
            )}
            {!!task.requiredUploads?.length && (
              <div className="text-[9px] font-[700] text-amber-700">
                {isEs ? 'Uploads requeridos:' : 'Required uploads:'} {task.requiredUploads.join(', ')}
              </div>
            )}
          </div>
          <div className="text-[9px] font-[700] text-[#9E9890] mb-1">
            {isEs ? 'Evento de inspección' : 'Inspection event'}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={() => logInspectionEvent('requested')} disabled={saving}
              className="border border-[#ECEAE4] rounded-full py-1 text-[9px] font-[800] text-[#4B5563] disabled:opacity-40">
              {isEs ? 'Solicitada' : 'Requested'}
            </button>
            <button onClick={() => logInspectionEvent('scheduled')} disabled={saving}
              className="border border-[#ECEAE4] rounded-full py-1 text-[9px] font-[800] text-[#4B5563] disabled:opacity-40">
              {isEs ? 'Programada' : 'Scheduled'}
            </button>
            <button onClick={() => logInspectionEvent('passed')} disabled={saving}
              className="border border-emerald-200 bg-emerald-50 rounded-full py-1 text-[9px] font-[800] text-emerald-700 disabled:opacity-40">
              {isEs ? 'Aprobada' : 'Passed'}
            </button>
            <button onClick={() => logInspectionEvent('failed')} disabled={saving}
              className="border border-red-200 bg-red-50 rounded-full py-1 text-[9px] font-[800] text-red-700 disabled:opacity-40">
              {isEs ? 'Falló' : 'Failed'}
            </button>
            {isJurisdictionInspection && (
              <button onClick={() => logInspectionEvent('retry')} disabled={saving}
                className="border border-amber-200 bg-amber-50 rounded-full py-1 text-[9px] font-[800] text-amber-800 disabled:opacity-40">
                {isEs ? 'Reintento' : 'Retry'}
              </button>
            )}
            <button onClick={() => logInspectionEvent('rescheduled')} disabled={saving}
              className="border border-sky-200 bg-sky-50 rounded-full py-1 text-[9px] font-[800] text-sky-700 disabled:opacity-40">
              {isEs ? 'Reprogramada' : 'Rescheduled'}
            </button>
          </div>
          {/* Correction path: if an inspection was marked passed/completed by
              mistake, move it back to Pending. Logs a 'reopen' event (history is
              appended, never deleted). Shown only once the inspection is
              COMPLETED; a failed (BLOCKED) inspection uses "Mark unblocked". */}
          {task.status === 'COMPLETED' && (
            <div className="mt-2 pt-2 border-t border-[#F0EEE9]">
              <div className="text-[9px] font-[600] text-[#9E9890] mb-1">
                {isEs ? '¿Marcada por error?' : 'Marked by mistake?'}
              </div>
              <button onClick={() => logInspectionEvent('reopen')} disabled={saving}
                className="w-full border border-[#D8D2C8] bg-white rounded-full py-1 text-[9px] font-[800] text-[#4B5563] disabled:opacity-40">
                {isEs ? 'Reabrir inspección' : 'Reopen inspection'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Visible failure feedback — never a silent error. Input above is
          preserved so the user can retry. */}
      {saveError && (
        <div
          data-testid="gantt-edit-error"
          className="flex items-start gap-1.5 rounded-lg border border-red-300 bg-red-50 px-2 py-1.5 text-[10px] font-[700] text-red-700"
        >
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>{saveError}</span>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-1.5 text-[10px] font-[800] text-[#9E9890]">
          {isEs ? 'Cancelar' : 'Cancel'}
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 bg-[#06539B] text-white rounded-full py-1.5 text-[10px] font-[800] disabled:opacity-40">
          {saving ? '...' : isEs ? 'Guardar' : 'Save'}
        </button>
      </div>
    </div>
  )
}
